import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { fetchAllArtifacts } from '../../utils/artifactsService';
import { JobWithArtifacts } from '../../types';
import { Download, ChevronDown, ChevronRight, Package } from 'lucide-react';
import { downloadArtifact } from '../../utils/artifactsService';

interface GroupedArtifacts {
  [year: string]: {
    [month: string]: {
      [pipelineId: string]: {
        pipelineName: string;
        versions: {
          [version: string]: JobWithArtifacts[];
        };
      };
    };
  };
}

export default function ArtifactsPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupedArtifacts, setGroupedArtifacts] = useState<GroupedArtifacts>({});
  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [expandedPipelines, setExpandedPipelines] = useState<Set<string>>(new Set());
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadArtifacts();
  }, []);

  const loadArtifacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const jobs = await fetchAllArtifacts();
      
      // Group by Year > Month > Pipeline > Version
      const grouped: GroupedArtifacts = {};
      
      jobs.forEach(job => {
        const date = new Date(job.started_at);
        const year = date.getFullYear().toString();
        const month = date.toLocaleString('default', { month: 'long' });
        const pipelineId = job.pipeline_id;
        const version = job.pipeline_version;
        
        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = {};
        if (!grouped[year][month][pipelineId]) {
          grouped[year][month][pipelineId] = {
            pipelineName: job.pipeline_name,
            versions: {},
          };
        }
        if (!grouped[year][month][pipelineId].versions[version]) {
          grouped[year][month][pipelineId].versions[version] = [];
        }
        
        grouped[year][month][pipelineId].versions[version].push(job);
      });
      
      setGroupedArtifacts(grouped);
    } catch (err: any) {
      setError(err?.message || 'Failed to load artifacts');
    } finally {
      setLoading(false);
    }
  };

  const toggleYear = (year: string) => {
    setExpandedYears(prev => {
      const newSet = new Set(prev);
      if (newSet.has(year)) {
        newSet.delete(year);
      } else {
        newSet.add(year);
      }
      return newSet;
    });
  };

  const toggleMonth = (yearMonth: string) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(yearMonth)) {
        newSet.delete(yearMonth);
      } else {
        newSet.add(yearMonth);
      }
      return newSet;
    });
  };

  const togglePipeline = (key: string) => {
    setExpandedPipelines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleVersion = (key: string) => {
    setExpandedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        Loading artifacts...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{
          background: theme.errorLight,
          color: theme.error,
          border: `1px solid ${theme.error}`,
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
        }}>
          {error}
        </div>
        <button
          onClick={loadArtifacts}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const years = Object.keys(groupedArtifacts).sort((a, b) => parseInt(b) - parseInt(a));

  if (years.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 24, marginBottom: 8 }}>Artifacts</h1>
        <p style={{ color: theme.textSecondary, marginBottom: 24 }}>
          Browse and download artifacts from completed jobs
        </p>
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 12,
          padding: 48,
          textAlign: 'center',
        }}>
          <Package size={48} color={theme.textSecondary} style={{ margin: '0 auto 16px' }} />
          <p style={{ color: theme.textSecondary, fontSize: 16, margin: 0 }}>
            No artifacts found
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ color: theme.text, fontSize: 24, marginBottom: 8 }}>Artifacts</h1>
      <p style={{ color: theme.textSecondary, marginBottom: 24 }}>
        Browse and download artifacts from completed jobs, organized by date, pipeline, and version
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {years.map(year => {
          const isYearExpanded = expandedYears.has(year);
          const months = Object.keys(groupedArtifacts[year]).sort((a, b) => {
            const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            return monthOrder.indexOf(b) - monthOrder.indexOf(a);
          });

          return (
            <div key={year}>
              {/* Year Header */}
              <button
                    onClick={() => toggleYear(year)}
                style={{
                  width: '100%',
                  padding: 16,
                  background: theme.cardBackground,
                  border: `2px solid ${theme.border}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 20,
                  fontWeight: 700,
                  color: theme.text,
                  textAlign: 'left',
                }}
              >
                {isYearExpanded ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
                {year}
              </button>

              {/* Months */}
              {isYearExpanded && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {months.map(month => {
                    const yearMonthKey = `${year}-${month}`;
                    const isMonthExpanded = expandedMonths.has(yearMonthKey);
                    const pipelines = Object.keys(groupedArtifacts[year][month]);

                    return (
                      <div key={yearMonthKey}>
                        {/* Month Header */}
                        <button
                          onClick={() => toggleMonth(yearMonthKey)}
                          style={{
                            width: '100%',
                            padding: 12,
                            background: theme.background,
                            border: `1px solid ${theme.border}`,
                            borderRadius: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            fontSize: 16,
                            fontWeight: 600,
                            color: theme.text,
                            textAlign: 'left',
                          }}
                        >
                          {isMonthExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                          {month} {year}
                        </button>

                        {/* Pipelines */}
                        {isMonthExpanded && (
                          <div style={{ marginLeft: 20, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {pipelines.map(pipelineId => {
                              const pipelineKey = `${yearMonthKey}-${pipelineId}`;
                              const isPipelineExpanded = expandedPipelines.has(pipelineKey);
                              const pipelineData = groupedArtifacts[year][month][pipelineId];
                              const versions = Object.keys(pipelineData.versions).sort((a, b) => b.localeCompare(a));

                              return (
                                <div key={pipelineKey}>
                                  {/* Pipeline Header */}
                                  <button
                                    onClick={() => togglePipeline(pipelineKey)}
                                    style={{
                                      width: '100%',
                                      padding: 10,
                                      background: theme.cardBackground,
                                      border: `1px solid ${theme.border}`,
                                      borderRadius: 6,
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: theme.text,
                                      textAlign: 'left',
                                    }}
                                  >
                                    {isPipelineExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    {pipelineData.pipelineName}
                                  </button>

                                  {/* Versions */}
                                  {isPipelineExpanded && (
                                    <div style={{ marginLeft: 16, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                      {versions.map(version => {
                                        const versionKey = `${pipelineKey}-${version}`;
                                        const isVersionExpanded = expandedVersions.has(versionKey);
                                        const jobs = pipelineData.versions[version];

                                        return (
                                          <div key={versionKey}>
                                            {/* Version Header */}
                                            <button
                                              onClick={() => toggleVersion(versionKey)}
                                              style={{
                                                width: '100%',
                                                padding: 8,
                                                background: theme.background,
                                                border: `1px solid ${theme.border}`,
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                fontSize: 13,
                                                fontWeight: 500,
                                                color: theme.text,
                                                textAlign: 'left',
                                              }}
                                            >
                                              {isVersionExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                              Version {version} ({jobs.length} job{jobs.length !== 1 ? 's' : ''})
                                            </button>

                                            {/* Jobs with Artifacts */}
                                            {isVersionExpanded && (
                                              <div style={{ marginLeft: 12, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                {jobs.map(job => (
                                                  <div
                                                    key={job.job_id}
                                                    style={{
                                                      padding: 10,
                                                      background: theme.cardBackground,
                                                      border: `1px solid ${theme.border}`,
                                                      borderRadius: 4,
                                                    }}
                                                  >
                                                    <div style={{
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      alignItems: 'center',
                                                      marginBottom: 8,
                                                    }}>
                                                      <div>
                                                        <div style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>
                                                          Job: {job.job_id.substring(0, 8)}...
                                                        </div>
                                                        <div style={{ fontSize: 11, color: theme.textSecondary }}>
                                                          {new Date(job.started_at).toLocaleString()}
                                                        </div>
                                                      </div>
                                                      <button
                                                        onClick={() => navigate(`/jobs/${job.job_id}`)}
                                                        style={{
                                                          padding: '4px 8px',
                                                          background: 'transparent',
                                                          border: `1px solid ${theme.border}`,
                                                          borderRadius: 4,
                                                          cursor: 'pointer',
                                                          color: theme.accent,
                                                          fontSize: 11,
                                                          fontWeight: 600,
                                                        }}
                                                      >
                                                        View Job
                                                      </button>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                      {job.artifacts.map((artifact, idx) => (
                                                        <div
                                                          key={idx}
                                                          style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: 6,
                                                            background: theme.background,
                                                            borderRadius: 4,
                                                          }}
                                                        >
                                                          <div style={{ fontSize: 12, color: theme.text }}>
                                                            {artifact.name}
                                                          </div>
                                                          {!artifact.is_directory && (
                                                            <button
                                                              onClick={async () => {
                                                                try {
                                                                  await downloadArtifact(job.job_id, artifact.path);
                                                                } catch (error) {
                                                                  console.error('Failed to download artifact:', error);
                                                                  alert('Failed to download artifact. Please try again.');
                                                                }
                                                              }}
                                                              style={{
                                                                padding: '4px 8px',
                                                                background: theme.buttonBackground,
                                                                color: theme.buttonText,
                                                                border: 'none',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                textDecoration: 'none',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                              }}
                                                            >
                                                              <Download size={12} />
                                                              Download
                                                            </button>
                                                          )}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
